import './RestaurantOfTheDay.scss';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { SCROLL_ALL_RESTAURANTS } from '../../homeScroll';
import move_right from './img/move_right.png';
import { SectionHeading } from '../Typography';
import { fetchRestaurantsOfTheDay, restaurantDetailPath } from '../../services/api/restaurantService';
import { DINDIN_ASSET_ORIGIN } from '../../services/api/config';
import generalBanner from '../AllRestaurants/img/general_banner.png';

const SCROLL_EDGE_EPS = 4;
const SCROLL_ANIM_MS = 600;
const DEFAULT_TILE_STRIDE_PX = 260;

function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

function resolveMediaUrl(url) {
  const u = (url || '').trim();
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/')) return `${DINDIN_ASSET_ORIGIN}${u}`;
  return `${DINDIN_ASSET_ORIGIN}/${u}`;
}

function heroImageForRestaurant(r) {
  const resolved = resolveMediaUrl(r.pick_of_the_day_banner_url || r.banner_url);
  return resolved || generalBanner;
}

function useRotdSmoothScroll(scrollRef, updateScrollMeta) {
  const rafRef = useRef(0);

  const cancel = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
  }, []);

  const scrollByDelta = useCallback(
    (delta) => {
      const el = scrollRef.current;
      if (!el || !delta) return;

      cancel();

      const start = el.scrollLeft;
      const maxLeft = el.scrollWidth - el.clientWidth;
      const target = Math.max(0, Math.min(start + delta, maxLeft));
      const change = target - start;
      if (Math.abs(change) < 0.5) {
        updateScrollMeta();
        return;
      }

      const reduceMotion =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) {
        el.scrollLeft = target;
        updateScrollMeta();
        return;
      }

      const t0 = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - t0) / SCROLL_ANIM_MS);
        el.scrollLeft = start + change * easeInOutQuad(t);
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          rafRef.current = 0;
          updateScrollMeta();
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    },
    [scrollRef, cancel, updateScrollMeta],
  );

  useEffect(() => () => cancel(), [cancel]);

  return { scrollByDelta, cancelScrollAnimation: cancel };
}

export default function RestaurantOfTheDay() {
  const scrollRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrollMeta, setScrollMeta] = useState({
    hasOverflow: false,
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateScrollMeta = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScrollLeft = scrollWidth - clientWidth;
    const hasOverflow = maxScrollLeft > SCROLL_EDGE_EPS;
    const canScrollLeft = hasOverflow && scrollLeft > SCROLL_EDGE_EPS;
    const canScrollRight = hasOverflow && scrollLeft < maxScrollLeft - SCROLL_EDGE_EPS;
    setScrollMeta({ hasOverflow, canScrollLeft, canScrollRight });
  }, []);

  const { scrollByDelta } = useRotdSmoothScroll(scrollRef, updateScrollMeta);

  const scrollStepPx = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return DEFAULT_TILE_STRIDE_PX;
    const first = el.querySelector('.home-pick-strip__cell');
    if (!first) return DEFAULT_TILE_STRIDE_PX;
    const cs = getComputedStyle(el);
    const gap = parseFloat(cs.gap || cs.columnGap || '12') || 12;
    const w = first.getBoundingClientRect().width;
    return Math.max(120, Math.round(w + gap));
  }, []);

  useLayoutEffect(() => {
    if (loading || !items.length) return;
    updateScrollMeta();
  }, [items, loading, updateScrollMeta]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || loading || !items.length) return undefined;
    const ro = new ResizeObserver(() => updateScrollMeta());
    ro.observe(el);
    return () => ro.disconnect();
  }, [items.length, loading, updateScrollMeta]);

  const scrollLeft = useCallback(() => {
    scrollByDelta(-scrollStepPx());
  }, [scrollByDelta, scrollStepPx]);

  const scrollRight = useCallback(() => {
    scrollByDelta(scrollStepPx());
  }, [scrollByDelta, scrollStepPx]);

  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { restaurants } = await fetchRestaurantsOfTheDay({
          globalLoading: false,
          signal: ac.signal,
        });
        if (!cancelled) setItems(Array.isArray(restaurants) ? restaurants : []);
      } catch (e) {
        if (e.name === 'AbortError') return;
        if (!cancelled) setError(e.message || 'Could not load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, []);

  if (loading || error || !items.length) {
    return null;
  }

  const renderTiles = () =>
    items.map((r) => {
      const to = restaurantDetailPath(r);
      const src = heroImageForRestaurant(r);
      const name = (r.name || 'Restaurant').trim();
      return (
        <div className="home-pick-strip__cell" key={r.id}>
          <Link to={to} className="home-pick-strip__link restaurant-of-the-day__link">
            <img
              src={src}
              alt={name}
              className="home-pick-strip__media restaurant-of-the-day__img restaurant_of_the_day_img"
              loading="lazy"
              onError={(e) => {
                const img = e.currentTarget;
                if (img.dataset.fallbackApplied === '1') return;
                img.dataset.fallbackApplied = '1';
                img.src = generalBanner;
              }}
            />
          </Link>
        </div>
      );
    });

  const viewAllArrow = (
    <div className="home-pick-strip__arrow move_right_div">
      <Link to="/" state={SCROLL_ALL_RESTAURANTS} className="restaurant-of-the-day__chevron-link" aria-label="View all restaurants">
        <img src={move_right} alt="" className="move_right" />
      </Link>
    </div>
  );

  return (
    <div className="container">
      <div className="top_cusines restaurant-of-the-day home-pick-strip">
        <SectionHeading>RESTAURANT OF THE DAY</SectionHeading>
        <div className="restaurant-of-the-day__strip-row">
          <button
            type="button"
            className="restaurant-of-the-day__scroll-btn restaurant-of-the-day__scroll-btn--prev"
            onClick={scrollLeft}
            disabled={!scrollMeta.hasOverflow || !scrollMeta.canScrollLeft}
            aria-label="Scroll restaurant of the day left"
          >
            <img src={move_right} alt="" className="restaurant-of-the-day__scroll-btn-icon" />
          </button>
          <div className="restaurant-of-the-day__scroll-wrap">
            <div
              ref={scrollRef}
              className="restaurant-of-the-day__scroll"
              onScroll={updateScrollMeta}
            >
              {renderTiles()}
            </div>
          </div>
          <button
            type="button"
            className="restaurant-of-the-day__scroll-btn restaurant-of-the-day__scroll-btn--next"
            onClick={scrollRight}
            disabled={!scrollMeta.hasOverflow || !scrollMeta.canScrollRight}
            aria-label="Scroll restaurant of the day right"
          >
            <img src={move_right} alt="" className="restaurant-of-the-day__scroll-btn-icon" />
          </button>
          {viewAllArrow}
        </div>
      </div>
    </div>
  );
}
